import React from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  breadcrumbs?: BreadcrumbItem[];
  /** Primary page actions, right-aligned on desktop and full-width on phones. */
  action?: React.ReactNode;
  subtitle?: string;
  /** Small mono label above the title, e.g. the section name. */
  eyebrow?: string;
  className?: string;
}

export default function PageHeader({
  title,
  breadcrumbs,
  action,
  subtitle,
  eyebrow,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("mb-6 flex flex-col gap-4 md:mb-7", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        // The shell's top bar already shows "portal › page"; repeat the trail on
        // wider screens only when it goes deeper than that.
        <nav aria-label="Breadcrumb" className={cn(breadcrumbs.length <= 2 && "md:hidden")}>
          <ol className="flex flex-wrap items-center gap-1 text-caption text-muted-foreground">
            {breadcrumbs.map((item, i) => (
              <li key={`${item.label}-${i}`} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="size-3 opacity-60" aria-hidden />}
                {item.path ? (
                  <Link
                    to={item.path as any}
                    className="rounded-xs transition-colors hover:text-foreground"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="text-body">
                    {item.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
          <h1 className="text-[24px] leading-[32px] font-medium tracking-[-0.6px] text-foreground md:text-[28px] md:leading-9">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 max-w-2xl text-sm leading-6 text-body">{subtitle}</p>
          )}
        </div>
        {action && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 max-sm:[&>*]:flex-1">
            {action}
          </div>
        )}
      </div>
    </header>
  );
}
